import { useToast } from 'primevue/usetoast'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useBuildStore } from '@/stores/BuildStore'

export function useBuildChecker() {
  const toast = useToast()
  const { t } = useI18n()
  const router = useRouter()
  const buildStore = useBuildStore()
  
  const checkInterval = ref<NodeJS.Timeout | null>(null)
  const CHECK_INTERVAL_MS = 2 * 60 * 1000

  const checkForUpdates = async () => {
    if (buildStore.isChecking) {
      return
    }
    
    buildStore.setCheckingState(true)
    const hasNewBuild = await buildStore.checkForNewBuild()
      
    if (hasNewBuild && buildStore.shouldShowToast) {
        showUpdateToast()
        buildStore.markToastAsShown()
        buildStore.updateStoredBuild(buildStore.latestBuild || 'initial')
      }
    buildStore.setCheckingState(false)
  }

  const showUpdateToast = () => {
    toast.add({
      severity: 'warn',
      summary: t('build.updateAvailable.title'),
      detail: t('build.updateAvailable.message'),
      life: 0,
      closable: false,
      group: 'newBuild'
    })
  }

  const startBuildChecking = async () => {
    setTimeout(checkForUpdates, CHECK_INTERVAL_MS)
    checkInterval.value = setInterval(checkForUpdates, CHECK_INTERVAL_MS)
  }

  const stopBuildChecking = () => {
    if (checkInterval.value) {
      clearInterval(checkInterval.value)
      checkInterval.value = null
    }
  }
  
  watch(
    () => router.currentRoute.value.fullPath,
    () => {
      toast.removeGroup('newBuild')
    }
  )

  onMounted(() => {
    startBuildChecking()
  })

  onUnmounted(() => {
    stopBuildChecking()
  })

  return {
    checkForUpdates,
    buildStore,
    isCheckingBuild: () => buildStore.isChecking
  }
}
